/**
 * DocumentModal - Modal for viewing and editing documents.
 */

import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Eye, Edit3, Trash2, Save, Loader2, Download, FileText } from 'lucide-react';
import { Button, Badge, TabsList, Tab, IconContainer } from '@/components/ui';
import { MarkdownPreview, DocumentEditor } from '@/components/markdown';
import type { SavedDocument } from '@/lib/types';
import { exportDocumentAsJson, downloadAsJson, downloadAsMarkdown, downloadAsText } from '@/lib/documentExport';
import { getCategoryIcon, formatDate, getCategoryIconVariant } from '../utils';

interface DocumentModalProps {
  document: SavedDocument;
  editedContent: string;
  reviewMode: 'preview' | 'edit' | 'transcription';
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  transcriptionContent: string | null;
  transcriptionLoading: boolean;
  transcriptionError: string | null;
  onClose: () => void;
  onContentChange: (content: string) => void;
  onModeChange: (mode: 'preview' | 'edit' | 'transcription') => void;
  onSave: () => void;
  onDelete: (docId: string) => void;
  onCheckboxToggle?: (lineIndex: number, checked: boolean) => void;
}

export const DocumentModal: React.FC<DocumentModalProps> = ({
  document,
  editedContent,
  reviewMode,
  hasUnsavedChanges,
  isSaving,
  isDeleting,
  transcriptionContent,
  transcriptionLoading,
  transcriptionError,
  onClose,
  onContentChange,
  onModeChange,
  onSave,
  onDelete,
  onCheckboxToggle,
}) => {
  const { t } = useTranslation();
  const Icon = getCategoryIcon(document.category);
  const iconVariant = getCategoryIconVariant(document.category);

  const handleDownloadMarkdown = useCallback(() => {
    const content = editedContent || document.content;
    downloadAsMarkdown(content, document.title);
  }, [editedContent, document]);

  const handleDownloadJson = useCallback(() => {
    const content = editedContent || document.content;
    const exported = exportDocumentAsJson(content, document);
    downloadAsJson(exported, document.title);
  }, [editedContent, document]);

  const handleDownloadTranscription = useCallback(() => {
    if (transcriptionContent) {
      downloadAsText(transcriptionContent, document.title);
    }
  }, [transcriptionContent, document.title]);

  const hasTranscription = !!document.transcriptionId;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <IconContainer variant={iconVariant} size="sm" className="flex-shrink-0">
              <Icon className="w-5 h-5" />
            </IconContainer>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2 className="text-base sm:text-xl font-bold text-slate-900 truncate">
                  {document.title}
                </h2>
                {hasUnsavedChanges && (
                  <Badge variant="warning" size="sm">{t('projects.unsaved')}</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-500 mt-0.5">
                {document.projectName && (
                  <>
                    <span>{document.projectName}</span>
                    <span className="hidden sm:inline">•</span>
                  </>
                )}
                {document.templateType && <span>{document.templateType}</span>}
                <span className="hidden sm:inline">•</span>
                <span>{formatDate(document.createdAt)}</span>
              </div>
            </div>
          </div>

          {/* Mode Toggle */}
          <div className="flex items-center gap-2 mr-2">
            <TabsList variant="contained">
              <Tab
                variant="contained"
                active={reviewMode === 'preview'}
                onClick={() => onModeChange('preview')}
              >
                <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{t('projects.preview')}</span>
              </Tab>
              <Tab
                variant="contained"
                active={reviewMode === 'edit'}
                onClick={() => onModeChange('edit')}
              >
                <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{t('projects.edit')}</span>
              </Tab>
              {hasTranscription && (
                <Tab
                  variant="contained"
                  active={reviewMode === 'transcription'}
                  onClick={() => onModeChange('transcription')}
                >
                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{t('projects.transcription')}</span>
                </Tab>
              )}
            </TabsList>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label={t('projects.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {reviewMode === 'preview' ? (
            <div className="px-4 sm:px-6 py-4">
              <MarkdownPreview content={editedContent || document.content} onCheckboxToggle={onCheckboxToggle} />
            </div>
          ) : reviewMode === 'edit' ? (
            <div className="h-full">
              <DocumentEditor
                value={editedContent}
                onChange={onContentChange}
                placeholder={t('projects.editorPlaceholder')}
                minHeight="min-h-[400px]"
                enableAiRefine={true}
              />
            </div>
          ) : (
            <div className="px-4 sm:px-6 py-4">
              {transcriptionLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">{t('projects.transcriptionLoading')}</p>
                  </div>
                </div>
              ) : transcriptionError ? (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-600">
                  {transcriptionError}
                </div>
              ) : transcriptionContent ? (
                <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-lg p-4 border border-slate-200">
                  {transcriptionContent}
                </pre>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">{t('projects.transcriptionEmpty')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-200 bg-slate-50 sm:rounded-b-2xl" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(document.id)}
              disabled={isDeleting || isSaving}
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4" />
              {t('projects.delete')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadMarkdown}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{t('projects.downloadMd')}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadJson}
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">{t('projects.downloadJson')}</span>
            </Button>
            {reviewMode === 'transcription' && transcriptionContent && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownloadTranscription}
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t('projects.downloadTranscription')}</span>
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <Button
                variant="primary"
                size="sm"
                onClick={onSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('projects.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {t('projects.saveChanges')}
                  </>
                )}
              </Button>
            )}
            <Button
              variant="dark"
              size="sm"
              onClick={onClose}
            >
              {hasUnsavedChanges ? t('projects.discard') : t('projects.close')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;
